import {
  Banner,
  Box,
  InputDescription,
  InputLabel,
  Modal,
  OptionType,
  SelectMenu,
  Text,
  TextInput,
} from '@fullstory/components';
import { intl } from '@fullstory/intl';
import { DescriptionMarkdownPreview } from '@fullstory/junk-drawer-ui';
import { Visibility } from '@fullstory/proto/fs/gcloud/dstore/protoauditstore';
import { Dashboard, ObjectType, Space } from '@fullstory/proto/fs/services/dx-objects';
import { useNotifications } from '@fullstory/react-hooks';
import { fullstorySelectors } from '@fullstory/redux';
import React, { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { MAX_DESCRIPTION_LENGTH, PLURAL_OBJECT_TYPES } from '../../constants';
import { useActiveSpaceContext } from '../../contexts';
import { useDefaultSpace, useSpaces } from '../../hooks';
import { SpaceAndSectionSelect, SpaceAndSectionSelectProps } from '../SpaceAndSectionSelect';

const { isDebugMode } = fullstorySelectors;

const VISIBILITY_OPTIONS: OptionType<Visibility, {}>[] = [
  { key: 'Private', label: 'Private', value: Visibility.PRIVATE },
  { key: 'Public', label: 'Public', value: Visibility.PUBLIC },
];

type ACTION_TYPE = 'duplicate' | 'save' | 'share' | 'saveAs';

export interface SaveAndShareModalProps
  extends Omit<
    SpaceAndSectionSelectProps,
    'touchedSpaceSelect' | 'setTouchedSpaceSelect' | 'touchedSectionSelect' | 'variant'
  > {
  /**
   * Optional visibility because an unsaved object won't yet have a visibility
   * undefined will render the modal in save mode, otherwise it will render in share mode
   */
  visibility?: Visibility;
  privateDependentObjects: ObjectType[];
  name: string;
  description?: string;
  onCloseSignal: () => void;
  open: boolean;
  onSave: (
    name: string,
    visibility: Visibility,
    dashboard: Dashboard | null,
    space?: Space,
    section?: string,
    description?: string,
    hasPrivateSegment?: boolean,
  ) => void;
  // 'save', 'duplicate' or 'share'
  action: ACTION_TYPE;
  returnToDashboard: Dashboard | null;
  infoText?: ReactNode;
}

export const SaveAndShareModal: React.FC<SaveAndShareModalProps> = ({
  visibility: initialVisibility,
  name,
  description,
  onCloseSignal,
  onSave,
  privateDependentObjects = [],
  open,
  action,
  returnToDashboard,
  onSpaceSelect,
  selectedSpace: initialSpace,
  infoText,
  ...rest
}) => {
  const [visibility, setVisibility] = useState(initialVisibility ?? Visibility.PUBLIC);
  const [touchedSpaceSelect, setTouchedSpaceSelect] = useState(false);
  const [touchedSectionSelect, setTouchedSectionSelect] = useState(false);
  const [saveAsName, setSaveAsName] = useState(name);
  const [saveAsDescription, setSaveAsDescription] = useState(description);
  const { activeSpace } = useActiveSpaceContext();
  const debugMode = useSelector(isDebugMode);

  useEffect(() => {
    setSaveAsName(name);
    setSaveAsDescription(description);
  }, [name, description]);

  // Note: We recalculate the default visibility whenever a new modal is loaded,
  // including when the action changes.
  useEffect(() => {
    // If there is an active space section, default Visibility to PUBLIC.
    setVisibility(
      activeSpace?.sectionId ? Visibility.PUBLIC : initialVisibility ?? Visibility.PUBLIC,
    );
  }, [initialVisibility, activeSpace]);

  const { objectType, disableSpaceSelect, selectedSection, sectionRequired } = rest;
  const { spaces } = useSpaces(true);
  const defaultSpace = useDefaultSpace();
  const [selectedSpace, setSelectedSpace] = useState<Space | undefined>(defaultSpace);

  const isShareMode = action === 'share';
  const disableLocation =
    disableSpaceSelect ||
    (isShareMode && initialVisibility === Visibility.PUBLIC) ||
    visibility === Visibility.PRIVATE;

  const nameError = useMemo(() => action === 'saveAs' && !saveAsName, [action, saveAsName]);
  const [descriptionError, setDescriptionError] = useState(false);

  const objectTypeLabel = intl.formatMessage(
    {
      defaultMessage:
        '{objectType, select, 1 {metric} 2 {funnel} 3 {retention} 5 {segment} 6 {dashboard} 7 {heatmap} 8 {journey} 16 {table} other {unknown type}}',
      id: 'hZiNdQ',
    },
    { objectType },
  );

  const mixedVisibilityMessage = useMemo(() => {
    const intlObjectList = intl.formatList(
      privateDependentObjects.map(o => PLURAL_OBJECT_TYPES[o].toLowerCase()),
    );

    return intl.formatMessage(
      {
        defaultMessage:
          'This {objectTypeLabel} contains private {objectList}. All {objectList} will also be made public.',
        id: 'cQAJp+',
        description:
          'objectList will contain internationalized list of objects which correspond to a fullstory object type e.g metrics, funnels, heatmaps, etc',
      },
      { objectTypeLabel, objectList: intlObjectList },
    );
  }, [privateDependentObjects, objectTypeLabel]);

  const onSpaceSelectFunc = useCallback(
    (option: OptionType<string, {}> | null) => {
      if (option) {
        onSpaceSelect(option);
        setSelectedSpace(spaces.find(s => s.id === option.value));
        setTouchedSpaceSelect(true);
      }
    },
    [onSpaceSelect, spaces],
  );

  useEffect(() => {
    if (initialSpace) {
      onSpaceSelectFunc(initialSpace);
    }
  }, [onSpaceSelectFunc, initialSpace]);

  const onSaveFunc = useCallback(() => {
    if (!disableLocation && !selectedSpace && visibility === Visibility.PUBLIC) {
      setTouchedSpaceSelect(true);
    } else if (!selectedSection && sectionRequired && visibility === Visibility.PUBLIC) {
      setTouchedSectionSelect(true);
    } else {
      onSave(
        saveAsName,
        debugMode ? Visibility.PUBLIC : visibility,
        returnToDashboard,
        visibility == Visibility.PUBLIC ? selectedSpace : undefined,
        visibility == Visibility.PUBLIC ? selectedSection?.value : undefined,
        saveAsDescription,
      );
      // reset these values so that the modal is in a clean state when it opens again
      setTouchedSpaceSelect(false);
      setTouchedSectionSelect(false);
      setVisibility(initialVisibility ?? Visibility.PRIVATE);
      setSelectedSpace(defaultSpace);
    }
  }, [
    defaultSpace,
    disableLocation,
    initialVisibility,
    onSave,
    returnToDashboard,
    saveAsDescription,
    saveAsName,
    sectionRequired,
    selectedSection,
    selectedSpace,
    visibility,
    debugMode,
  ]);

  const onCancel = useCallback(() => {
    setVisibility(initialVisibility ?? Visibility.PRIVATE);
    onCloseSignal();
  }, [initialVisibility, onCloseSignal]);

  let headingText = intl.formatMessage({ defaultMessage: 'Share', id: 'OKhRC6' });
  if (action == 'duplicate') {
    headingText = intl.formatMessage(
      {
        defaultMessage: 'Duplicate {objectTypeLabel}',
        id: 'utESy1',
      },
      { objectTypeLabel },
    );
  } else if (!isShareMode) {
    headingText = intl.formatMessage(
      {
        defaultMessage: 'Save your {objectTypeLabel}',
        id: 'ZyDX9L',
      },
      { objectTypeLabel },
    );
  }

  const notifications = useNotifications();

  return (
    <Modal
      data-component="SaveAndShareModal"
      onCloseSignal={onCancel}
      open={open}
      size="small"
      testId="save-and-share-modal"
    >
      <Modal.Header>
        <Text appearance="bodyLargeBold" color="emphasized">
          {headingText}
        </Text>
      </Modal.Header>
      <Modal.Body>
        {infoText && (
          <Box marginBottom={24}>
            <Text appearance="bodyMedium">{infoText}</Text>
          </Box>
        )}
        {/* Name and maybe description input for saving */}
        {action == 'saveAs' && (
          <>
            <Box marginBottom={24}>
              <InputLabel htmlFor="name-input">
                <Text appearance="bodyMediumBold" color="emphasized">
                  <FormattedMessage defaultMessage="Name" id="HAlOn1" />
                </Text>
              </InputLabel>
              <TextInput
                value={saveAsName}
                id="name-input"
                testId="name-input"
                onChange={e => setSaveAsName(e.target.value)}
                invalid={nameError}
              />
              {nameError && (
                <InputDescription
                  invalid
                  description={intl.formatMessage({
                    defaultMessage: 'Name is required',
                    id: 'Gvxoji',
                  })}
                />
              )}
            </Box>
            {description && (
              <Box marginBottom={24}>
                <InputLabel htmlFor="dashboard-description-textarea" optional>
                  <Text appearance="bodyMediumBold" color="emphasized" tag="span">
                    <FormattedMessage defaultMessage="Description" id="Q8Qw5B" />
                  </Text>
                </InputLabel>
                <DescriptionMarkdownPreview
                  id="dashboard-description-textarea"
                  testId="description"
                  rows={3}
                  onChange={e => setSaveAsDescription(e.target.value)}
                  setSaveDisabled={setDescriptionError}
                  value={saveAsDescription ?? ''}
                  placeholder={intl.formatMessage({
                    defaultMessage: 'Dashboard description',
                    id: 'MHb4RF',
                  })}
                  maxLength={MAX_DESCRIPTION_LENGTH}
                />
              </Box>
            )}
          </>
        )}
        {/* visibility selection */}
        <Box marginTop={4} display="flex" alignItems="center">
          <Box paddingRight={12}>
            <Box marginBottom={4}>
              <Text appearance="bodyMediumBold" color="emphasized">
                <FormattedMessage defaultMessage="Visibility" id="JAkIqb" />
              </Text>
            </Box>
            <Text appearance="bodySmall" color="secondary">
              <FormattedMessage
                defaultMessage="This object is {private, select, true {only visible to you and it can only be added to a private dashboard and your home page} other {visible to anyone in your organization and can be placed on any space or dashboard}}."
                id="gyTufY"
                values={{
                  private: visibility === Visibility.PRIVATE,
                }}
              />
            </Text>
          </Box>
          <Box flex={1}>
            <SelectMenu
              testId="visibility-select"
              size="small"
              variant="outline"
              selected={VISIBILITY_OPTIONS.find(
                o => o.value === (debugMode ? Visibility.PUBLIC : visibility),
              )}
              onChange={o => setVisibility(o?.value ?? Visibility.PRIVATE)}
              options={VISIBILITY_OPTIONS}
              disabled={(action === 'share' && visibility === Visibility.PUBLIC) || debugMode}
            />
          </Box>
        </Box>

        {/* space and section selection */}
        {!disableLocation && (
          <Box marginTop={24}>
            <Text appearance="bodyMediumBold" color="emphasized">
              <FormattedMessage defaultMessage="Location" id="rvirM2" />
            </Text>
            <Box marginTop={12}>
              <SpaceAndSectionSelect
                selectedSpace={initialSpace}
                touchedSpaceSelect={touchedSpaceSelect}
                setTouchedSpaceSelect={setTouchedSpaceSelect}
                touchedSectionSelect={touchedSectionSelect}
                variant="fill-builder"
                onSpaceSelect={onSpaceSelectFunc}
                {...rest}
              />
            </Box>
          </Box>
        )}
        {debugMode && (
          <Box marginTop={24}>
            <Banner
              title={intl.formatMessage({
                defaultMessage: 'Private objects disabled',
                id: 'LGjgxh',
              })}
              description={intl.formatMessage({
                defaultMessage: 'Impersonators are not allowed to create private objects',
                id: 'LraA9F',
              })}
              variant="warning"
            />
          </Box>
        )}
        {privateDependentObjects.length === 0 && visibility === Visibility.PUBLIC && (
          <Box marginTop={24}>
            <Banner
              title={intl.formatMessage({
                defaultMessage: 'Making an object public',
                id: 'ePKlcp',
              })}
              description={intl.formatMessage(
                {
                  defaultMessage:
                    'Once {isPublic, select, true {an object has been set} other {this object is set}} to public, it cannot be {isPublic, select, true {reverted to private} other {changed back to private in the future}}.',
                  id: 'psinah',
                },
                {
                  isPublic: initialVisibility === Visibility.PUBLIC,
                },
              )}
              variant="default"
            />
          </Box>
        )}
        {privateDependentObjects.length > 0 && visibility === Visibility.PUBLIC && (
          <Box marginTop={24}>
            <Banner
              title={intl.formatMessage({
                defaultMessage: 'Mixed object visibility',
                id: 'py7KXK',
              })}
              description={mixedVisibilityMessage}
              variant="warning"
            />
          </Box>
        )}
      </Modal.Body>
      <Modal.FooterActions
        primaryAction={{
          text: intl.formatMessage({ defaultMessage: 'Save', id: 'jvo0vs' }),
          action: onSaveFunc,
          disabled:
            (visibility == initialVisibility && name == saveAsName && isShareMode) ||
            descriptionError ||
            nameError,
        }}
        secondaryAction={{
          text: intl.formatMessage({ defaultMessage: 'Cancel', id: '47FYwb' }),
          action: onCancel,
        }}
        tertiaryAction={
          isShareMode && initialVisibility === Visibility.PUBLIC
            ? {
                text: intl.formatMessage({ defaultMessage: 'Copy Link', id: 'u5aHb4' }),
                action: () => {
                  window.navigator.clipboard.writeText(window.location.href);
                  notifications.success(
                    intl.formatMessage({ defaultMessage: 'Copied to clipboard.', id: '8qZ4HE' }),
                  );
                },
                variant: 'outline',
              }
            : undefined
        }
      />
    </Modal>
  );
};
